import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraCheckboxField, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LifecycleNodeKind, useLinkedNodes } from '../../../components/LinkedChips';
import { projectDataClient } from '../../../lib/connect/clients';
import { proposalHref } from '../../../lib/proposalHref';
import { Choice } from '../PastPerformance/Fields';
import { accessDenied } from './outputPresentationModel';
const ids = Testids.captureOutputHandoff;
export function OutputHandoff({ captureProjectId, onOpenProposal }) {
    const location = useLocation();
    const templateId = new URLSearchParams(location.search).get('outputTemplate') || 'capture-review-v1';
    const links = useLinkedNodes(LifecycleNodeKind.PURSUIT, captureProjectId);
    const versions = useQuery({
        queryKey: [
            'capture-output-lifecycle',
            captureProjectId,
            templateId,
            'handoff-versions'
        ],
        queryFn: ()=>projectDataClient.listCaptureOutputVersions({
                captureProjectId,
                templateId
            }),
        retry: false
    });
    const [versionId, setVersionId] = useState('');
    const [scope, setScope] = useState('whole');
    const [sectionKeys, setSectionKeys] = useState([]);
    const [proposalId, setProposalId] = useState('');
    const [busy, setBusy] = useState(false);
    const pending = useRef(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const choices = (links.data?.edges ?? []).flatMap(({ node })=>node?.kind === LifecycleNodeKind.PROPOSAL ? [
            {
                value: node.zouProjectId || node.id,
                label: node.label || 'Linked Proposal'
            }
        ] : []);
    const selected = versions.data?.versions.find((v)=>v.id === versionId);
    const destination = proposalId || choices[0]?.value || '';
    const emptySelection = scope === 'selected' && !sectionKeys.length;
    async function send() {
        if (pending.current || !selected || !destination || emptySelection) return;
        pending.current = true;
        setBusy(true);
        setError('');
        setMessage('');
        try {
            await projectDataClient.createCaptureOutputHandoff({
                captureProjectId,
                proposalProjectId: destination,
                outputVersionId: selected.id,
                sectionKeys: scope === 'selected' ? sectionKeys : []
            });
            setMessage('Baseline saved. Proposal keeps this exact version when Capture is regenerated.');
        } catch (cause) {
            setError(accessDenied(cause) ? 'You need permission to update this Pursuit and Proposal and read the selected sources. Request access, then retry.' : 'The baseline could not be saved. Reload Outputs and retry your selection.');
        } finally{
            pending.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": ids.root,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Use in Proposal · reusable upstream intelligence"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Proposal chooses its working content separately. This baseline is not an approved technical volume or submitted price. Recipients need permission to read the Capture content and sources."
                }),
                links.isLoading || versions.isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
                    className: "h-16 w-full"
                }) : links.isError || versions.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    children: "Outputs or linked Proposals could not be loaded. Reload this page to retry."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        !choices.length ? /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: onOpenProposal,
                            children: "Open Proposal"
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Choice, {
                                    label: "Linked Proposal",
                                    testId: ids.proposal,
                                    value: destination,
                                    onChange: setProposalId,
                                    options: choices
                                }),
                                /*#__PURE__*/ _jsx(Choice, {
                                    label: "Exact output version",
                                    testId: ids.version,
                                    value: selected?.id || '',
                                    onChange: (id)=>{
                                        setVersionId(id);
                                        setScope('whole');
                                        setSectionKeys([]);
                                        setMessage('');
                                        setError('');
                                    },
                                    options: [
                                        {
                                            value: '',
                                            label: 'Select an immutable version'
                                        },
                                        ...(versions.data?.versions ?? []).filter((v)=>!v.externalFile).map((v)=>({
                                                value: v.id,
                                                label: `Output version ${v.version} · ${v.createdAt}`
                                            }))
                                    ]
                                }),
                                selected && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Choice, {
                                            label: "Content to hand off",
                                            testId: ids.scope,
                                            value: scope,
                                            onChange: (value)=>{
                                                setScope(value);
                                                setSectionKeys([]);
                                                setMessage('');
                                                setError('');
                                            },
                                            options: [
                                                {
                                                    value: 'whole',
                                                    label: 'Whole version · all permitted sections'
                                                },
                                                {
                                                    value: 'selected',
                                                    label: 'Selected sections'
                                                }
                                            ]
                                        }),
                                        scope === 'selected' && /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    children: "Select at least one section."
                                                }),
                                                (selected.projection?.sections ?? []).map((section)=>/*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                                        className: "flex",
                                                        label: section.title,
                                                        "data-testid": ids.section,
                                                        "data-section-key": section.key,
                                                        controlProps: {
                                                            checked: sectionKeys.includes(section.key),
                                                            disabled: busy,
                                                            onCheckedChange: (checked)=>{
                                                                setSectionKeys((keys)=>checked === true ? [
                                                                        ...keys,
                                                                        section.key
                                                                    ] : keys.filter((key)=>key !== section.key));
                                                                setMessage('');
                                                                setError('');
                                                            }
                                                        }
                                                    }, section.key))
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": ids.send,
                                    disabled: busy || !selected || emptySelection,
                                    onClick: send,
                                    children: busy ? 'Saving baseline…' : 'Use selected version in Proposal'
                                })
                            ]
                        }),
                        !versions.data?.versions.length && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Generate an output before selecting a baseline."
                        })
                    ]
                }),
                error && /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    children: error
                }),
                message && /*#__PURE__*/ _jsxs(SpecraText, {
                    "aria-live": "polite",
                    "data-testid": ids.message,
                    children: [
                        message,
                        " ",
                        /*#__PURE__*/ _jsx(Link, {
                            to: proposalHref(destination),
                            children: "Open Proposal baseline"
                        })
                    ]
                })
            ]
        })
    });
}
